import { addressStatusForDeliveryCode, quadrantFor } from './delivery-status.js';
// Capacité fibre — dire si une unité demandée peut être ajoutée directement, ou si la demande part
// chez Build. Le calcul vit ici, et pas dans un écran, pour la même raison que delivery-status.ts et
// area.ts : la fenêtre de validation, la console DMP et le message vers Build doivent en donner UNE
// version, pas trois.
//
// ⛔ UNE FONCTION PURE, SANS ÉCRITURE NI ROUTAGE : son seul contrat est un verdict lisible. Ce sont les
// appelants qui en tirent un blocage — la garde de l'ajout de boîte (N3, 10-09) refuse un non-admin quand il
// reste 0, et rien d'autre. Le routage automatique vers Build (étage ③, code 2110) attend toujours HC.
//
// LES SOURCES, DANS L'ORDRE OÙ ELLES SONT ARRIVÉES. Le débrief interne PST du 06-08-2026 porte LE CALCUL (§04) ;
// la réunion Build du 03-09-2026 portait les plafonds ; le débrief capacité du 09-09-2026 réécrit QP et QDX
// (« fait foi, mais incomplet » — décision du user, 16-09) ; et pour la façade, COCON fait foi (user, 17-09).
//
// 📍 L'ÉCHELLE : UNE ADRESSE POSTALE, JAMAIS UN BÂTIMENT. C'est l'erreur de la première version, et
// elle a été vue en production le 04-09 sur le jvid 11815065. Un `belmap_building_id` groupe plusieurs
// adresses distinctes, et chacune a son propre delivery status donc son propre cadran, donc sa propre
// règle. Bâtiment 15329395 : « Rue Panier Jules 2 2A » = 1 unité en 9055 (cadran P, poteau) et « Rue
// Panier Jules 2 2D » = 4 unités en 8035 (cadran D, façade box). En agrégeant les cinq, l'app retenait le
// cadran MAJORITAIRE (D) et appliquait la règle de la façade à une adresse posée sur un POTEAU.
// L'appelant doit donc passer les unités d'UNE adresse — côté écran, un groupe de
// `groupApartmentsByAddress` ; côté ajout de boîte, les unités de la variante choisie. La réserve
// `mixedAddresses` ci-dessous existe pour attraper quiconque repasserait un bâtiment entier.
//
// LE CALCUL (débrief §04) — il se fait sur la BUILD LIST, jamais sur la deployment list :
//   construites = les unités portant scopelist_v2   (« Build list » = la capacité CONSTRUITE)
//   ajoutées    = les unités portant scopelist_v15 SANS scopelist_v2 (= déjà consommées)
// Le piège est nommé dans les sources, et Fred l'a rejoué en direct le 03-09 : MDU de 3, une unité déjà
// rajoutée → il reste 0, pas 1. Sans le décompte on accorde une capacité qui n'existe plus.
//
// LES RÈGLES PAR CADRAN — état au 2026-09-17 (C0.1) :
//  · QA/QC — 20 % de spare, minimum 1 : un budget d'AJOUTS, on ne retranche que les ajouts. Réunion 03-09
//    l.461 : « 20 % de la capacité du MDU, c'est si actuellement il y a 10 unités, ça fait deux unités ».
//    Arrondi au PLANCHER, et il se déduit : le « minimum 1 » n'aurait aucun sens avec un arrondi au plafond.
//  · QP — débrief du 09-09 : « s'il existe déjà un client connecté : ajout possible d'une seule unité ; sinon
//    on repasse en QD, avec installation d'un novux sur la façade ». Client connecté = au moins une unité DE
//    L'ADRESSE en Homes Connect (…6, ou 1113-1119). Une seule unité = 1 ajout AU TOTAL, ajouts déjà faits
//    décomptés. Sans client connecté, poser un novux c'est construire : TOUJOURS Build (décision du user du
//    16-09, contre la lecture « calcul QD » qui lui était proposée — ne pas la reproposer). Le « max 4 sur le
//    poteau » validé par Build le 03-09 n'est plus la règle.
//  · QD — la place RÉELLE du boîtier façade, lue dans COCON : 8 par boîtier − les fibres déjà reliées
//    (voisins compris) − les unités prévues de l'adresse. L'ancien « multiple de 8 − construites − ajoutées »
//    ignorait ce que le voisin consomme : sur le JVID 10328760 (17-09) la pastille disait 4 pour un boîtier
//    que COCON montrait plein, et l'ajout passait.
//  · QDX — débrief du 09-09 : « appartement avant et arrière : vérifier la position de l'unité ; en façade →
//    ajout possible si la capacité le permet ; à l'arrière → renvoi chez Build et refaire un SSV / TSA ».
//    L'app ne peut pas savoir où est l'unité : le RELECTEUR la déclare au moment de l'ajout. Façade → le
//    calcul QD ; arrière → Build. Tant qu'elle n'est pas déclarée (la pastille), on montre le calcul QD avec
//    la réserve qui le dit.
//
// « BUILD D'OFFICE » (QP sans client connecté, QDX à l'arrière) = il reste 0, sans « dépassement ». C'est ce qui
// garde UN SEUL prédicat de blocage, `available === 0` (N3) : ne pas en inventer un deuxième.
// ⛔ null ≠ 0. COCON illisible, aucun boîtier, aucun code de livraison connu → on ne sait pas, et on ne bloque
// pas : les bloquer fermerait des dossiers faute de donnée (les 51 adresses grises du 10-09).
//
// D'OÙ VIENT LE CADRAN — mesuré en prod le 2026-09-04. Le débrief §03 dit « la règle du cadran donné par le
// SITE SURVEY » : c'est la colonne `public.address.quadrant`. Elle porte A · C · D · P · M ET une famille
// « x » — Ax · Cx · Dx · Mx — que le code de livraison ne sait pas exprimer. **`Dx` EST le QDX.** La colonne
// voisine `quadrant_pst` (le cadran du PLAN) ne porte, elle, aucun DX : ce n'est pas la bonne source.
// Le cadran dérivé du code de livraison reste le REPLI (248 bâtiments MDU couverts sur 249 à eux deux), et
// la colonne gagne quand elle existe, parce que c'est elle que la règle métier désigne.
//
// LA FAMILLE « x ». **Tranché par le user le 2026-09-04 : `Ax` et `Cx` se traitent comme `A` et `C`.** `Dx`
// a sa propre règle (QDX, ci-dessus). Une variante jamais observée (`Px`…) lève `variantUndefined` : le jour où
// le DMP en sort une nouvelle, l'écran le dit au lieu de lui appliquer une règle en silence.
/** QA/QC : 20 % de spare, plancher de 1 — un MDU de 3 a d'office droit à 1 ajout même hors des 20 %. */
export const UNDERGROUND_SPARE_RATIO = 0.2;
export const UNDERGROUND_SPARE_MIN = 1;
/** QP avec un client déjà connecté : « ajout possible d'une seule unité » (débrief du 09-09), AU TOTAL. */
export const POLE_CONNECTED_ADDITIONS = 1;
/** Fibres d'un boîtier façade — un seul modèle dans COCON, « toujours 8 fibres » (user, 15-09). */
export const FACADE_BOX_PORTS = 8;
/** Le code de refus DMP à poser quand la demande part chez Build faute de capacité. IL EXISTE DÉJÀ
 *  (`NOT_HOMES_PASSED_REASONS`) : « Blocked by Unifiber due to a fiber capacity problem ». Rien à
 *  demander au DMP, c'était la crainte d'Anouk. */
export const CAPACITY_REFUSAL_CODE = 2110;
/** Ordre de présentation : d'abord ce qui invalide le calcul, ensuite ce qui le nuance. */
const RESERVE_ORDER = [
    'noBuildList', 'quadrantUnknown', 'connectedClientUnknown', 'coconUnavailable', 'noFacadeBoxInCocon',
    'noRuleForQuadrant', 'mixedQuadrants', 'mixedAddresses', 'variantUndefined', 'qdxPositionAtAdd',
    'spareContested',
];
/** Le cadran d'UNE unité, normalisé « majuscule + éventuel x minuscule » pour que « AX », « ax » et
 *  « Ax » ne fassent pas trois cadrans. La colonne du site survey gagne, le code de livraison replie. */
function unitQuadrant(u) {
    const raw = (u.quadrant ?? '').trim();
    const norm = (s) => s[0].toUpperCase() + s.slice(1).toLowerCase();
    if (raw !== '')
        return { q: norm(raw), survey: true };
    const derived = quadrantFor(u.deliveryStatus);
    return derived ? { q: norm(derived), survey: false } : null;
}
function quadrantCounts(units) {
    const counts = new Map();
    for (const u of units) {
        const r = unitQuadrant(u);
        if (!r)
            continue;
        const cur = counts.get(r.q) ?? { n: 0, survey: false };
        counts.set(r.q, { n: cur.n + 1, survey: cur.survey || r.survey });
    }
    return counts;
}
/** Le cadran majoritaire. Map conserve l'ordre d'insertion, donc une égalité retombe sur le premier
 *  cadran rencontré : le résultat est stable d'un appel à l'autre. */
function dominant(counts) {
    let best = null;
    let bestN = 0;
    let survey = false;
    for (const [q, t] of counts)
        if (t.n > bestN) {
            best = q;
            bestN = t.n;
            survey = t.survey;
        }
    return best == null ? null : { quadrant: best, source: survey ? 'survey' : 'delivery' };
}
/** Le cadran retenu pour une adresse. Il se lit sur les unités CONSTRUITES — ce sont elles qui portent le
 *  réseau posé — avec repli sur l'ensemble quand aucune ne l'est, sinon un bâtiment neuf n'aurait jamais de
 *  cadran. Exporté : c'est lui qui dit à un appelant s'il doit lire COCON AVANT de calculer. */
export function capacityQuadrant(units) {
    const built = units.filter((u) => !!u.scopelistV2);
    return dominant(quadrantCounts(built.length ? built : units));
}
/** QD ou QDX : le calcul a besoin du boîtier façade lu dans COCON. Lecture lente (~1,7 s mesurées par le user
 *  le 15-09) : les appelants ne la font que pour ces cadrans-là. */
export function capacityNeedsCocon(units) {
    return capacityQuadrant(units)?.quadrant[0] === 'D';
}
/** QDX : l'ajout de boîte doit demander au relecteur où se trouve l'unité. */
export function capacityNeedsPosition(units) {
    const q = capacityQuadrant(units)?.quadrant;
    return q != null && q[0] === 'D' && q[1] === 'x';
}
/** En scope : dans la deployment list ou la build list, et pas retirée. Même définition que le renumbering. */
function unitInScope(u) {
    return (!!u.scopelistV15 || !!u.scopelistV2) && !u.outOfScopeAt;
}
/**
 * Le boîtier façade d'une adresse, à partir de la réponse COCON. La réponse peut couvrir PLUSIEURS adresses
 * (la fenêtre de validation lit COCON une fois pour tout le ticket) : on ne garde que les boîtiers où arrive
 * une fibre d'une unité de CETTE adresse, et ses propres unités prévues.
 */
export function summarizeFacadeCocon(response, units) {
    const own = new Set();
    for (const u of units)
        if (u.jvid != null && Number.isInteger(u.jvid) && u.jvid > 0)
            own.add(u.jvid);
    const boxes = response.boxes.filter((b) => b.jvids.some((j) => own.has(j)));
    const connected = boxes.reduce((n, b) => n + b.jvids.length, 0);
    const free = boxes.reduce((n, b) => n + Math.max(0, FACADE_BOX_PORTS - b.jvids.length), 0);
    const unlinked = new Set(response.unlinked);
    const planned = [...new Set(units
            .filter((u) => u.jvid != null && unlinked.has(u.jvid) && unitInScope(u))
            .map((u) => u.jvid))];
    return {
        boxes,
        ports: boxes.length * FACADE_BOX_PORTS,
        connected,
        free,
        planned,
        available: Math.max(0, free - planned.length),
        missing: Math.max(0, planned.length - free),
    };
}
/**
 * La capacité disponible sur une adresse, et ce qu'il faut en penser.
 *
 * Règle d'ensemble du débrief §04, appliquée telle quelle : si la capacité ne suffit pas pour TOUTES
 * les unités demandées, c'est la demande ENTIÈRE qui part chez Build. Jamais de création partielle —
 * « il ne faut pas en créer une ou deux puis se retrouver bloqué pour la dernière ».
 */
export function computeCapacity(input) {
    const units = input.units ?? [];
    const requested = input.requested ?? null;
    const reserves = [];
    const built = units.filter((u) => !!u.scopelistV2).length;
    const added = units.filter((u) => !u.scopelistV2 && !!u.scopelistV15).length;
    const dom = capacityQuadrant(units);
    const quadrant = dom?.quadrant ?? null;
    const quadrantSource = dom?.source ?? null;
    // L'hétérogénéité s'évalue sur TOUTES les unités, pas seulement sur les construites : c'est l'alerte
    // « delivery statuts hétérogènes » demandée le 03-09 (cas 8001 mêlé à 8015).
    if (quadrantCounts(units).size > 1)
        reserves.push('mixedQuadrants');
    // L'adresse COMPLÈTE, pas le nom de rue : l'extension est ce qui sépare « 2 2A » de « 2 2D », et
    // c'est exactement là que la version précédente ne voyait rien. Le numéro seul ne suffirait pas non
    // plus — les deux valent « 2 ». Il faut les trois champs.
    // ⚠️ L'extension est NEUTRALISÉE quand elle répète le numéro : dans le DMP `house_number_extension`
    // n'est pas une extension mais le numéro COMPLET, égal au numéro dans 92 % des cas. Sans ça, « 2 »
    // + ext « 2 » et « 2 » + ext absente compteraient pour deux adresses, et la réserve crierait au loup
    // sur une adresse unique. C'est la même convention que l'affichage, à dessein.
    const addressKey = (u) => {
        const n = (v) => (v ?? '').trim().toLowerCase();
        const house = n(u.houseNumber);
        const ext = n(u.houseNumberExtension);
        return `${n(u.streetName)}|${house}|${ext === house ? '' : ext}`;
    };
    const addresses = new Set(units.map(addressKey).filter((a) => a !== '||'));
    if (addresses.size > 1)
        reserves.push('mixedAddresses');
    let rule = 'none';
    let cap = null;
    let capKind = null;
    let facade = null;
    /** « Build d'office » : il reste 0, quoi qu'on compte. */
    let forcedBuild = false;
    // La lettre porte la règle, le « x » porte la nuance.
    const base = quadrant?.[0] ?? null;
    const variant = quadrant != null && quadrant.length > 1 && quadrant[1] === 'x';
    if (base === 'A' || base === 'C') {
        // `Ax` et `Cx` passent ici sans rien changer : décision du user du 04-09, ils VALENT `A` et `C`
        // pour la capacité. Pas de réserve — la variante n'est plus une inconnue.
        rule = 'spare20';
        capKind = 'additions';
        cap = Math.max(UNDERGROUND_SPARE_MIN, Math.floor(built * UNDERGROUND_SPARE_RATIO));
        reserves.push('spareContested');
    }
    else if (base === 'P') {
        if (variant)
            reserves.push('variantUndefined');
        // Le client connecté se lit sur les codes CONNUS. Une unité ajoutée n'a souvent pas encore de code (Cas-1,
        // « delivery empty ») : l'ignorer ne change rien ; mais sans AUCUN code, on ne sait pas — et « on ne sait
        // pas » n'est pas « aucun client », qui enverrait chez Build faute de donnée.
        const known = units.filter((u) => u.deliveryStatus != null);
        if (known.some((u) => addressStatusForDeliveryCode(u.deliveryStatus) === 'HOMES_CONNECT')) {
            rule = 'pole1';
            capKind = 'additions';
            cap = POLE_CONNECTED_ADDITIONS;
        }
        else if (known.length === 0) {
            reserves.push('connectedClientUnknown');
        }
        else {
            rule = 'poleBuild';
            forcedBuild = true;
        }
    }
    else if (base === 'D') {
        if (variant && input.unitPosition === 'REAR') {
            rule = 'qdxRear';
            forcedBuild = true;
        }
        else {
            // QD, ou QDX en façade (déclarée ou pas encore) : la place réelle du boîtier, lue dans COCON.
            rule = 'facadeCocon';
            capKind = 'total';
            if (variant && input.unitPosition !== 'FACADE')
                reserves.push('qdxPositionAtAdd');
            if (input.facade == null || input.facade === 'unavailable') {
                reserves.push('coconUnavailable');
            }
            else if (input.facade.boxes.length === 0) {
                reserves.push('noFacadeBoxInCocon');
            }
            else {
                facade = input.facade;
                cap = facade.ports;
            }
        }
    }
    else if (base === 'M') {
        // Multifu = du SDU. Le SDU relève d'une règle de PORTÉE réseau (« le réseau doit passer devant la
        // maison »), pas d'un comptage — débrief §02, explicitement hors périmètre.
        reserves.push('noRuleForQuadrant');
    }
    else {
        reserves.push('quadrantUnknown');
    }
    // Sans une seule unité en build list, il n'y a rien de construit à partager : ni plafond, ni verdict — pas
    // même un « Build d'office ». Le garde-fou n'est pas décoratif : le plancher de 1 du QA/QC accorderait un
    // ajout sur un bâtiment jamais construit, et c'est aussi ce qui rend juste le repli sur l'instantané (DMP
    // injoignable → attributs nuls → « on ne sait pas »). Le cadran et la règle, eux, restent affichés.
    let available;
    let over = false;
    if (built === 0) {
        reserves.push('noBuildList');
        cap = null;
        facade = null;
        available = null;
    }
    else if (forcedBuild) {
        available = 0;
    }
    else if (rule === 'facadeCocon') {
        available = facade ? facade.available : null;
        over = facade != null && facade.missing > 0;
    }
    else if (cap != null) {
        const consumed = capKind === 'additions' ? added : built + added;
        const raw = cap - consumed;
        over = raw < 0;
        available = Math.max(0, raw);
    }
    else {
        available = null;
    }
    const verdict = available == null ? 'unknown'
        : requested != null ? (requested <= available ? 'ok' : 'build')
            : (available > 0 ? 'ok' : 'build');
    return {
        quadrant,
        quadrantSource,
        rule,
        built,
        added,
        cap,
        capKind,
        available,
        over,
        requested,
        verdict,
        reserves: RESERVE_ORDER.filter((r) => reserves.includes(r)),
        facade,
    };
}
//# sourceMappingURL=capacity.js.map